import { lazy, Suspense, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
// Eager: landing/auth (1º paint, não-autenticado) + layouts (sempre presentes nas áreas logadas)
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import { AppShell } from "./components/layout/AppShell";
import { InvestorShell } from "./components/layout/InvestorShell";

// Wave 21: code-split — cada página vira chunk próprio (carregada sob demanda via Suspense).
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DashboardV2 = lazy(() => import("./pages/DashboardV2"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const ConciliationPage = lazy(() => import("./pages/domains").then((m) => ({ default: m.ConciliationPage })));
const CnpjPage = lazy(() => import("./pages/domains").then((m) => ({ default: m.CnpjPage })));
const CnpjDetailPage = lazy(() => import("./pages/domains").then((m) => ({ default: m.CnpjDetailPage })));
const FingerprintPage = lazy(() => import("./pages/domains").then((m) => ({ default: m.FingerprintPage })));
const DataprevPage = lazy(() => import("./pages/domains").then((m) => ({ default: m.DataprevPage })));
const LossesPage = lazy(() => import("./pages/Losses"));
const VintagePage = lazy(() => import("./pages/Vintage"));
const RatePage = lazy(() => import("./pages/Rate"));
const CotasPage = lazy(() => import("./pages/Cotas"));
const SimulationsPage = lazy(() => import("./pages/Simulations"));
const EstruturacaoFidcPage = lazy(() => import("./pages/EstruturacaoFidc"));
const RegionalRiskPage = lazy(() => import("./pages/RegionalRisk"));
const PddPage = lazy(() => import("./pages/Pdd"));
const AdminCompaniesPage = lazy(() => import("./pages/AdminCompanies"));
const AdminAuditPage = lazy(() => import("./pages/AdminAudit"));
const ProfilePage = lazy(() => import("./pages/Profile"));
const ImportsPage = lazy(() => import("./pages/Imports"));
const MacroStatsPage = lazy(() => import("./pages/MacroStats"));
const ReportsPage = lazy(() => import("./pages/Reports"));
const AgingPage = lazy(() => import("./pages/Aging"));
const EnquadramentoPage = lazy(() => import("./pages/Enquadramento"));
const Ifrs9Page = lazy(() => import("./pages/Ifrs9"));
const AuditInternalPage = lazy(() => import("./pages/AuditInternal"));
const AdminFidcTranchesPage = lazy(() => import("./pages/AdminFidcTranches"));
const PaymentSchedulePage = lazy(() => import("./pages/PaymentSchedule"));
const ReconMatchingPage = lazy(() => import("./pages/ReconMatching"));
const AdminAutomationPage = lazy(() => import("./pages/AdminAutomation"));
const AdminSystemHealthPage = lazy(() => import("./pages/AdminSystemHealth"));
const LoanTapePage = lazy(() => import("./pages/LoanTape"));
const LoanTapeDetailPage = lazy(() => import("./pages/LoanTapeDetail"));
const FiagroPage = lazy(() => import("./pages/Fiagro"));
const VertBucketsPage = lazy(() => import("./pages/VertBuckets"));
const VertFundoPage = lazy(() => import("./pages/VertFundo"));
const AdminInterestRateForecast = lazy(() => import("./pages/AdminInterestRateForecast"));
const CvmResearch = lazy(() => import("./pages/admin/CvmResearch"));
const RelatoriosRisco = lazy(() => import("./pages/admin/RelatoriosRisco"));
const InformacoesMercado = lazy(() => import("./pages/admin/InformacoesMercado"));
const DadosTransacoes = lazy(() => import("./pages/admin/DadosTransacoes"));
const AnaliseGestoras = lazy(() => import("./pages/admin/AnaliseGestoras"));
const AnaliseAdministrador = lazy(() => import("./pages/admin/AnaliseAdministrador"));
const AgenteCobrancaPage = lazy(() => import("./pages/AgenteCobranca"));
const AgenteCobrancaDetailPage = lazy(() => import("./pages/AgenteCobrancaDetail"));
const DistributionsPage = lazy(() => import("./pages/Distributions"));
const StressTestPage = lazy(() => import("./pages/StressTest"));
const SubordinacaoLiquidezPage = lazy(() => import("./pages/SubordinacaoLiquidez"));
const ConcessoesPage = lazy(() => import("./pages/Concessoes"));
const RollRatePage = lazy(() => import("./pages/RollRate"));
const OriginadorasPage = lazy(() => import("./pages/Originadoras"));
const OriginadoraTrajetoriaPage = lazy(() => import("./pages/OriginadoraTrajetoria"));
const AdminTrajectoryQualityPage = lazy(() => import("./pages/AdminTrajectoryQuality"));
const RecompraPage = lazy(() => import("./pages/Recompra"));
const KycTrailPage = lazy(() => import("./pages/KycTrail"));
const PeerBenchmarkPage = lazy(() => import("./pages/PeerBenchmark"));
const InvestorProfilePage = lazy(() => import("./pages/investor/Profile"));
const InvestorPositionsPage = lazy(() => import("./pages/investor/Positions"));
const InvestorDistributionsPage = lazy(() => import("./pages/investor/Distributions"));
const InvestorNavHistoryPage = lazy(() => import("./pages/investor/NavHistory"));
const InvestorLaminaPage = lazy(() => import("./pages/investor/Lamina"));
const PublicFidcPage = lazy(() => import("./pages/PublicFidcPage"));

function CnpjDetailRoute() {
  const { cnpj } = useParams<{ cnpj: string }>();
  return cnpj ? <CnpjDetailPage cnpj={cnpj} /> : <Navigate to="/app/cnpj" replace />;
}

function RouteFallback() {
  return <div className="min-h-[40vh] flex items-center justify-center text-muted-foreground text-sm">Carregando…</div>;
}
import { SessionProvider, useSession, type SessionState } from "./lib/session";
import { FidcProvider } from "./lib/fidc-context";
import { ToastProvider } from "./components/ui/toast";
import type { AuthUser } from "./lib/api";

function RequireAuth({ children }: { children: (user: AuthUser) => ReactNode }) {
  const { session } = useSession();
  const location = useLocation();
  if (session.status === "loading") {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Carregando...</div>;
  }
  if (session.status !== "authenticated") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children(session.user)}</>;
}

function RequireAdmin({ user, children }: { user: AuthUser; children: ReactNode }) {
  if (user.role !== "admin") return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function GuestOnly({ children, session }: { children: ReactNode; session: SessionState }) {
  if (session.status === "authenticated") {
    const target = session.user.role === "cotista" ? "/investidor/posicoes" : "/app";
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
}

function RequireCotista({ children }: { children: (user: AuthUser) => ReactNode }) {
  const { session } = useSession();
  const location = useLocation();
  if (session.status === "loading") {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Carregando...</div>;
  }
  if (session.status !== "authenticated") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (session.user.role !== "cotista") {
    return <Navigate to="/app" replace />;
  }
  return <>{children(session.user)}</>;
}

function AppRoutes() {
  const { session } = useSession();
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      {/* Página pública por fundo — sem autenticação, indexável por crawlers */}
      <Route path="/fidc/:slug" element={<Suspense fallback={<RouteFallback />}><PublicFidcPage /></Suspense>} />
      <Route path="/login" element={<GuestOnly session={session}><Login /></GuestOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/app/*"
        element={
          <RequireAuth>
            {(user) => (
              <FidcProvider>
              <AppShell user={user}>
                <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="" element={<DashboardV2 user={user} />} />
                  <Route path="cockpit" element={<DashboardV2 user={user} />} />
                  <Route path="dashboard-classic" element={<Dashboard user={user} />} />
                  <Route path="losses" element={<LossesPage />} />
                  <Route path="aging" element={<AgingPage />} />
                  <Route path="enquadramento" element={<EnquadramentoPage />} />
                  <Route path="ifrs9" element={<Ifrs9Page />} />
                  <Route path="audit-internal" element={<AuditInternalPage />} />
                  <Route path="pdd" element={<PddPage />} />
                  <Route path="vintage" element={<VintagePage />} />
                  <Route path="payment-schedule" element={<PaymentSchedulePage />} />
                  <Route path="rate" element={<RatePage />} />
                  <Route path="cotas" element={<CotasPage />} />
                  <Route path="profile" element={<ProfilePage user={user} />} />
                  <Route path="imports" element={<ImportsPage />} />
                  <Route path="macro-stats" element={<MacroStatsPage />} />
                  <Route path="reports" element={<ReportsPage user={user} />} />
                  <Route path="simulations" element={<SimulationsPage />} />
                  <Route path="estruturacao-fidc" element={<EstruturacaoFidcPage />} />
                  <Route path="regional-risk" element={<RegionalRiskPage />} />
                  <Route path="conciliation" element={<ConciliationPage />} />
                  <Route path="recon-matching" element={<ReconMatchingPage />} />
                  <Route path="cnpj" element={<CnpjPage />} />
                  <Route path="cnpj/:cnpj" element={<CnpjDetailRoute />} />
                  <Route path="fingerprint" element={<FingerprintPage />} />
                  <Route path="dataprev" element={<DataprevPage />} />
                  <Route path="loan-tape" element={<LoanTapePage user={user} />} />
                  <Route path="fiagro" element={<FiagroPage />} />
                  <Route path="fiagro/vert-buckets" element={<VertBucketsPage />} />
                  <Route path="fiagro/vert-fundo" element={<VertFundoPage />} />
                  <Route path="loan-tape/:id" element={<LoanTapeDetailPage user={user} />} />
                  <Route path="agente-cobranca" element={<AgenteCobrancaPage />} />
                  <Route path="agente-cobranca/:cnpj" element={<AgenteCobrancaDetailPage />} />
                  <Route path="distribuicoes" element={<DistributionsPage user={user} />} />
                  <Route path="stress-test" element={<StressTestPage user={user} />} />
                  <Route path="subordinacao-liquidez" element={<SubordinacaoLiquidezPage />} />
                  <Route path="concessoes" element={<ConcessoesPage />} />
                  <Route path="roll-rate" element={<RollRatePage />} />
                  <Route path="originadoras" element={<OriginadorasPage />} />
                  <Route path="originadora/:cnpj/trajetoria" element={<OriginadoraTrajetoriaPage user={user} />} />
                  <Route path="admin/trajectory-quality" element={<RequireAdmin user={user}><AdminTrajectoryQualityPage /></RequireAdmin>} />
                  <Route path="admin/pesquisa-cvm" element={<RequireAdmin user={user}><CvmResearch /></RequireAdmin>} />
                  <Route path="admin/relatorios-risco" element={<RequireAdmin user={user}><RelatoriosRisco /></RequireAdmin>} />
                  <Route path="admin/informacoes-mercado" element={<RequireAdmin user={user}><InformacoesMercado /></RequireAdmin>} />
                  <Route path="dados-transacoes" element={<RequireAdmin user={user}><DadosTransacoes /></RequireAdmin>} />
                  <Route path="analise-gestoras" element={<RequireAdmin user={user}><AnaliseGestoras /></RequireAdmin>} />
                  <Route path="analise-administrador" element={<RequireAdmin user={user}><AnaliseAdministrador /></RequireAdmin>} />
                  <Route path="recompra" element={<RecompraPage />} />
                  <Route path="kyc" element={<KycTrailPage />} />
                  <Route path="peer-benchmark" element={<PeerBenchmarkPage />} />
                  <Route path="admin/users" element={<RequireAdmin user={user}><AdminUsers user={user} /></RequireAdmin>} />
                  <Route path="admin/companies" element={<RequireAdmin user={user}><AdminCompaniesPage /></RequireAdmin>} />
                  <Route path="admin/audit" element={<RequireAdmin user={user}><AdminAuditPage /></RequireAdmin>} />
                  <Route path="admin/fidc-tranches" element={<RequireAdmin user={user}><AdminFidcTranchesPage /></RequireAdmin>} />
                  <Route path="admin/automation" element={<RequireAdmin user={user}><AdminAutomationPage /></RequireAdmin>} />
                  <Route path="admin/system-health" element={<RequireAdmin user={user}><AdminSystemHealthPage /></RequireAdmin>} />
                  <Route path="admin/interest-rate-forecast" element={<RequireAdmin user={user}><AdminInterestRateForecast /></RequireAdmin>} />
                  <Route path="*" element={<Navigate to="/app" replace />} />
                </Routes>
                </Suspense>
              </AppShell>
              </FidcProvider>
            )}
          </RequireAuth>
        }
      />
      {/* Onda 12.3 — Portal do Cotista (subpath segregado) */}
      <Route
        path="/investidor/*"
        element={
          <RequireCotista>
            {(user) => (
              <InvestorShell user={user}>
                <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="" element={<Navigate to="posicoes" replace />} />
                  <Route path="perfil" element={<InvestorProfilePage />} />
                  <Route path="posicoes" element={<InvestorPositionsPage />} />
                  <Route path="distribuicoes" element={<InvestorDistributionsPage />} />
                  <Route path="nav" element={<InvestorNavHistoryPage />} />
                  <Route path="lamina" element={<InvestorLaminaPage />} />
                  <Route path="*" element={<Navigate to="/investidor/posicoes" replace />} />
                </Routes>
                </Suspense>
              </InvestorShell>
            )}
          </RequireCotista>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <SessionProvider>
      <ToastProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </ToastProvider>
    </SessionProvider>
  );
}
